// Libraries
import { Accordion, Box, Divider, Flex, Text } from '@chakra-ui/react';
import { FieldArray } from 'formik';
import isEqual from 'lodash.isequal';
import { Fragment, useEffect } from 'react';
// Components
import { SwitchControl, TextWithTooltip } from '@atoms/index';
import { AccordionItem, DropdownInput, FuelModeGroup, SliderInput } from '@molecules/index';
import { CiiSimulatorFormValues } from '@schemas/forms';
import { FormFooter, Legend, OptimizeAccordion, OptimizeAlert } from './components';
// Utils
import text from '@translations/en.json';
import { getStateFromFormValues } from './helpers';
// Types
import { setFuelModeGroupValues } from '@/src/utils/helpers';
import { RatingKeys } from '@globalTypes/index';
import { CiiSimulatorFormProps } from './CiiSimulatorForm.types';

/**
 * A form component for CII simulation with dynamic inputs and interactive feedback.
 * This component offers multiple configurations based on fuel type, carbon pricing, average Beaufort scale,
 * sailing days, underwater cleaning, and allows users to select optimized speeds and average speeds.
 *
 * @component
 * @param {CiiSimulatorFormProps} props - The props for the CiiSimulatorForm component.
 * @param {boolean} [props.isLoading=false] - Indicates if data related to the form is currently loading.
 * @param {boolean} [props.isSimulating=false] - Indicates if a simulation operation is in progress.
 * @param {boolean} [props.isOptimizing=false] - Indicates if an optimization operation is in progress.
 * @param {CiiFormData} props.formData - Data required for form operations, including defaults, optimized speeds, and parameter ranges.
 * @param {Function} props.setWasUpdated - Function to update the status of the form.
 * @param {Function} props.setIsOpen - Function to close the mobile simulator.
 * @param {Function} props.runSimulation - Function to initiate a simulation.
 * @param {Function} props.updateOptimizedSpeeds - Function to update optimized speeds based on form values.
 * @returns {ReactElement} The CiiSimulatorForm component.
 */
const CiiSimulatorForm = ({
  formikCtx,
  isLoading = false,
  isSimulating = false,
  isOptimizing = false,
  formData,
  setWasUpdated,
  runSimulation,
  updateOptimizedSpeeds,
}: CiiSimulatorFormProps) => {
  const { fuelOptions, optimizedSpeeds, parametersRanges, defaultParametersValues } = formData;
  const isProcessing = isLoading || isOptimizing || isSimulating;
  const showDualFuel = defaultParametersValues.fuelModes.length > 1;

  const getLoadingText = () => {
    if (isOptimizing) return text.cii_simulator.buttons.submit.optimizing;
    if (isSimulating) return text.cii_simulator.buttons.submit.simulating;
    return text.cii_simulator.buttons.submit.loading;
  };

  const resetHandler = async () => {
    runSimulation?.(getStateFromFormValues(defaultParametersValues));
    formikCtx.setValues({ ...defaultParametersValues, optimizedSpeeds });
    setWasUpdated(false);
  };

  const handleClick = () => {
    if (formikCtx.values.canOptimize === 0) {
      formikCtx.setFieldValue('canOptimize', 1);
    } else {
      formikCtx.setValues({
        ...formikCtx.values,
        ciiRating: undefined,
        canOptimize: 0,
      });
      updateOptimizedSpeeds?.(getStateFromFormValues(formikCtx.values));
    }
  };

  const isInitialSimulation = isEqual(formikCtx.values, defaultParametersValues);

  useEffect(() => {
    if (optimizedSpeeds && !isEqual(optimizedSpeeds, formikCtx.values.optimizedSpeeds)) {
      formikCtx.setValues({ ...formikCtx.values, optimizedSpeeds, canOptimize: 0 });
    }
    // NOTE: This effect only needs to run whe the new optimal speeds arrive
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optimizedSpeeds]);

  useEffect(() => {
    formikCtx.setValues({ ...formikCtx.values, optimizedSpeeds, canOptimize: 1 });
    // ?NOTE: we need to keep the accordion closed at the beginning
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      as='form'
      display='flex'
      flexDirection='column'
      alignItems='center'
      gap={{ base: 1.5, md: 3 }}
      onSubmit={formikCtx.handleSubmit}
      w='full'
      backgroundColor='white'
    >
      <Accordion w='full' defaultIndex={0}>
        <AccordionItem index={1} title={text.cii_simulator.accordion.title1} innerChakraProps={{ pt: 4 }}>
          <Flex mb={3}>
            <OptimizeAlert rating={formikCtx.values.ciiRating} />
          </Flex>
          {/* laden speed */}
          <SliderInput
            isLoading={isLoading}
            isDisabled={isProcessing}
            label={text.cii_simulator.slider_inputs.labels.laden_avg_speed}
            min={parametersRanges.averageSpeed.minValue}
            max={parametersRanges.averageSpeed.maxValue}
            initialValue={defaultParametersValues.ladenAvgSpeed}
            value={formikCtx.values.ladenAvgSpeed}
            step={0.1}
            precision={1}
            error={formikCtx.errors.ladenAvgSpeed}
            onChange={(value = '') => {
              if (value !== formikCtx.values.optimizedSpeeds[formikCtx.values.ciiRating as RatingKeys]?.ladenSpeedKts) {
                formikCtx.setValues({ ...formikCtx.values, ladenAvgSpeed: value, ciiRating: undefined });
              } else {
                formikCtx.setFieldValue('ladenAvgSpeed', value);
              }
            }}
          />

          {/* ballast speed */}
          <SliderInput
            isLoading={isLoading}
            isDisabled={isProcessing}
            label={text.cii_simulator.slider_inputs.labels.ballast_avg_speed}
            min={parametersRanges.averageSpeed.minValue}
            max={parametersRanges.averageSpeed.maxValue}
            initialValue={defaultParametersValues.ballastAvgSpeed}
            value={formikCtx.values.ballastAvgSpeed}
            step={0.1}
            precision={1}
            error={formikCtx.errors.ballastAvgSpeed}
            onChange={(value = '') => {
              if (
                value !== formikCtx.values.optimizedSpeeds[formikCtx.values.ciiRating as RatingKeys]?.ballastSpeedKts
              ) {
                formikCtx.setValues({ ...formikCtx.values, ballastAvgSpeed: value, ciiRating: undefined });
              } else {
                formikCtx.setFieldValue('ballastAvgSpeed', value);
              }
            }}
          />

          {/* Optimized ratings */}
          <OptimizeAccordion
            title={text.cii_simulator.buttons.controls.optimize_button}
            isLoading={isProcessing}
            formCtx={formikCtx}
            onClick={handleClick}
          />

          <Divider borderBottomWidth='2px' borderColor='border.100' my={4} />

          {/* Sailing Days */}
          <SliderInput
            isLoading={isLoading}
            isDisabled={isProcessing}
            label={text.cii_simulator.slider_inputs.labels.sailing_days}
            min={parametersRanges.daysSailingPerMonth.minValue}
            max={parametersRanges.daysSailingPerMonth.maxValue}
            initialValue={defaultParametersValues.sailingDays}
            value={formikCtx.values.sailingDays}
            error={formikCtx.errors.sailingDays}
            onChange={(value = '') => {
              formikCtx.setValues({
                ...formikCtx.values,
                sailingDays: value,
                ciiRating: undefined,
                canOptimize: 1,
                optimizedSpeeds: defaultParametersValues.optimizedSpeeds,
              });
            }}
          />

          {/* Avg Beaufort */}
          <SliderInput
            isLoading={isLoading}
            isDisabled={isProcessing}
            label={text.cii_simulator.slider_inputs.labels.avg_beaufort}
            min={parametersRanges.averageBeaufortScale.minValue}
            max={parametersRanges.averageBeaufortScale.maxValue}
            initialValue={defaultParametersValues.avgBeaufort}
            value={formikCtx.values.avgBeaufort}
            error={formikCtx.errors.avgBeaufort}
            onChange={(value = '') => {
              formikCtx.setValues({
                ...formikCtx.values,
                avgBeaufort: value,
                ciiRating: undefined,
                canOptimize: 1,
                optimizedSpeeds: defaultParametersValues.optimizedSpeeds,
              });
            }}
          />

          {/* UWC */}
          <SwitchControl
            w='full'
            isDisabled={isProcessing}
            isChecked={formikCtx.values.uwc}
            label={text.cii_simulator.slider_inputs.labels.uwc}
            onChange={() => {
              formikCtx.setValues({
                ...formikCtx.values,
                uwc: !formikCtx.values.uwc,
                ciiRating: undefined,
                canOptimize: 1,
                optimizedSpeeds: defaultParametersValues.optimizedSpeeds,
              });
            }}
          />
        </AccordionItem>

        <AccordionItem index={2} title={text.cii_simulator.accordion.title2} innerChakraProps={{ pt: 4, minH: 113 }}>
          <Text fontSize='s' color='grey.100' mb={1}>
            {text.cii_simulator.slider_inputs.labels.fuel_carbon_section}
          </Text>
          <Flex justify='space-between' my={4}>
            <TextWithTooltip fontSize='m' color='text.base' tooltipText='' whiteSpace='nowrap'>
              Fuel Settings
            </TextWithTooltip>
            {/* Bulkfuel */}
            {
              !showDualFuel ? <SwitchControl
              w='inherit'
              isDisabled={isProcessing}
              isChecked={formikCtx.values.bulkFuelEnabled}
              label={text.cii_simulator.bulkFuel.switch_control_label}
              onChange={() => {
                formikCtx.setValues({
                  ...formikCtx.values,
                  bulkFuelEnabled: !formikCtx.values.bulkFuelEnabled,
                  bulkFuelQuantity: '0',
                  bulkFuelPrice: '0',
                  bulkFuelType: undefined,
                });
              }}
            /> : null
            }
          </Flex>

          {showDualFuel ? (
            <Fragment>
              {/* Dual Fuel */}
              <FieldArray
                name='fuelModes'
                render={({ form }) =>
                  (formikCtx.values as CiiSimulatorFormValues).fuelModes.map((fuelMode, index) => (
                    <Box key={fuelMode.mode} mb={4}>
                      <FuelModeGroup
                        index={index}
                        title={text.cii_simulator.slider_inputs.labels.fuel_cost_settings}
                        label={text.cii_simulator.slider_inputs.labels.cost_mt}
                        fuelMode={fuelMode}
                        error={formikCtx.errors.fuelModes?.[index]?.percentage}
                        value={formikCtx.values.fuelModes?.[index]?.percentage}
                        onChange={(value?: string) => {
                          setFuelModeGroupValues(form, index, value);
                        }}
                        isDisabled={isProcessing}
                        isLoading={isLoading}
                      />
                    </Box>
                  ))
                }
              />

              <Divider borderBottomWidth='1px' borderColor='linkWater' my={4} />
            </Fragment>
          ) : (
            <Fragment>
              {/* Single Fuel */}
              {formikCtx.values.bulkFuelEnabled && (
                <>
                  <Text fontSize='s' color='grey.100' mb={1}>
                    {text.cii_simulator.bulkFuel.description}
                  </Text>
                  <DropdownInput
                    isLoading={isLoading}
                    isDisabled={isProcessing}
                    dropdownLabel={text.cii_simulator.bulkFuel.dropdown_label}
                    dropdownError={formikCtx.errors.bulkFuelType}
                    inputLabel={text.cii_simulator.slider_inputs.labels.cost_mt}
                    selectionLabel={text.cii_simulator.bulkFuel.dropdown_selection_label}
                    min={0}
                    max={10000}
                    inputValue={formikCtx.values.bulkFuelPrice}
                    quantityInputValue={formikCtx.values.bulkFuelQuantity}
                    options={fuelOptions}
                    error={formikCtx.errors.bulkFuelPrice}
                    quantityInputError={formikCtx.errors.bulkFuelQuantity}
                    onDropdownChange={(value) => {
                      formikCtx.setValues({
                        ...formikCtx.values,
                        bulkFuelType: value,
                        bulkFuelPrice: `${value.price}`,
                        ciiRating: undefined,
                        canOptimize: 1,
                        optimizedSpeeds: defaultParametersValues.optimizedSpeeds,
                      });
                    }}
                    onInputChange={(value) => {
                      formikCtx.setFieldValue('bulkFuelPrice', value);
                    }}
                    onQuantityInputChange={(value) => {
                      formikCtx.setFieldValue('bulkFuelQuantity', value);
                    }}
                    doubleInputEnable
                  />
                </>
              )}
              <DropdownInput
                isLoading={isLoading}
                isDisabled={isProcessing}
                dropdownLabel={text.cii_simulator.bulkFuel.default_dropdown_label}
                inputLabel={text.cii_simulator.slider_inputs.labels.cost_mt}
                min={0}
                max={10000}
                dropdownValue={formikCtx.values.fuelType}
                inputValue={formikCtx.values.fuelPrice}
                options={fuelOptions}
                error={formikCtx.errors.fuelPrice}
                onDropdownChange={(value) => {
                  formikCtx.setValues({
                    ...formikCtx.values,
                    fuelType: value,
                    fuelPrice: `${value.price}`,
                    ciiRating: undefined,
                    canOptimize: 1,
                    optimizedSpeeds: defaultParametersValues.optimizedSpeeds,
                  });
                }}
                onInputChange={(value) => {
                  formikCtx.setFieldValue('fuelPrice', value);
                }}
              />
              <Divider borderBottomWidth='1px' borderColor='linkWater' my={4} />
            </Fragment>
          )}

          {/* Carbon Price */}
          <SliderInput
            currency='$'
            isLoading={isLoading}
            isDisabled={isProcessing}
            label={text.cii_simulator.slider_inputs.labels.carbon_price.title}
            tooltipText={text.cii_simulator.slider_inputs.labels.carbon_price.title_tooltip}
            min={parametersRanges.carbonCredit.minValue}
            max={parametersRanges.carbonCredit.maxValue}
            initialValue={defaultParametersValues.carbonPrice}
            value={formikCtx.values.carbonPrice}
            step={10}
            error={formikCtx.errors.carbonPrice}
            onChange={(value?: string) => {
              formikCtx.setFieldValue('carbonPrice', value);
            }}
            maxW={24}
          />
        </AccordionItem>
      </Accordion>

      <Box px={6} mt={1} w='full'>
        <FormFooter
          loadingText={getLoadingText()}
          isLoading={isProcessing}
          isDisabled={!formikCtx.isValid || isInitialSimulation}
          onClick={() => {
            resetHandler();
          }}
        />

        <Legend />
      </Box>
    </Box>
  );
};

export default CiiSimulatorForm;
